/**
 * Speech Recognition Utility Functions
 *
 * Contains utility functions for browser detection, connectivity checks,
 * and permission handling for speech recognition.
 */

import type {
  SpeechRecognition,
  SpeechRecognitionEvent,
  SpeechRecognitionResult,
  SpeechRecognitionAlternative,
} from '../types/speech-recognition';

/**
 * Detects if the current browser is Brave
 */
export const isBraveBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  return (
    Boolean(
      (navigator as Navigator & { brave?: { isBrave?: boolean } }).brave
        ?.isBrave
    ) || navigator.userAgent.includes('Brave')
  );
};

/**
 * Checks if speech recognition is supported in the current browser
 */
export const isSpeechRecognitionSupported = (): boolean => {
  if (typeof globalThis === 'undefined') return false;

  const window = globalThis as unknown as Window;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

/**
 * Gets the appropriate SpeechRecognition constructor for the current browser
 */
export const getSpeechRecognitionConstructor = () => {
  const window = globalThis as unknown as Window;
  return window.webkitSpeechRecognition ?? window.SpeechRecognition;
};

/**
 * Checks network connectivity
 */
export const checkNetworkConnectivity = (): boolean => {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
};

/**
 * Requests microphone permission from the user
 */
export const requestMicrophonePermission = async (): Promise<boolean> => {
  if (!navigator.mediaDevices?.getUserMedia) return true;

  try {
    await navigator.mediaDevices.getUserMedia({ audio: true });
    return true;
  } catch {
    return false;
  }
};

/**
 * Configures a SpeechRecognition instance with appropriate settings
 */
export const configureSpeechRecognition = (
  recognition: SpeechRecognition,
  isBrave: boolean
): void => {
  recognition.continuous = false;
  recognition.interimResults = !isBrave;
  recognition.lang = 'en-US';
  recognition.maxAlternatives = 1;

  if (!isBrave && 'serviceURI' in recognition) {
    (recognition as SpeechRecognition & { serviceURI: string }).serviceURI =
      'wss://www.google.com/speech-api/v2/recognize';
  }
};

/**
 * Processes speech recognition results and extracts transcripts
 */
export const processSpeechResults = (
  event: SpeechRecognitionEvent,
  finalTranscriptRef: React.RefObject<string>,
  onResult?: (transcript: string) => void
): void => {
  let interimTranscript = '';
  let newFinalTranscript = '';

  for (let i = event.resultIndex; i < event.results.length; i++) {
    const result: SpeechRecognitionResult = event.results[i];
    const [alternative]: SpeechRecognitionAlternative[] = result;
    const { transcript } = alternative;

    if (result.isFinal) {
      newFinalTranscript += transcript;
    } else {
      interimTranscript += transcript;
    }
  }

  if (newFinalTranscript) {
    finalTranscriptRef.current += newFinalTranscript;
    onResult?.(finalTranscriptRef.current.trim());
  } else if (interimTranscript.trim() && onResult) {
    onResult((finalTranscriptRef.current + interimTranscript).trim());
  }
};
